import { Controller } from "@hotwired/stimulus";

// One last check before a support request is filed: if an article confidently
// answers the question, show it once and let the person choose.
//
// Everything here fails open. Flag off, no confident match, slow response, or a
// broken endpoint all submit the form normally — nobody gets stuck behind this
// on their way to asking for help. "Submit anyway" is a real, equal choice, not
// a confirmation to click through.
export default class extends Controller<HTMLFormElement> {
  static targets = ["panel", "formChrome", "title", "excerpt", "reason", "link", "submitAnyway", "shownSlug", "submit"];
  static values = { checkUrl: String, timeout: { type: Number, default: 6000 } };

  declare readonly panelTarget: HTMLElement;
  declare readonly formChromeTarget: HTMLElement;
  declare readonly titleTarget: HTMLElement;
  declare readonly excerptTarget: HTMLElement;
  declare readonly reasonTarget: HTMLElement;
  declare readonly linkTarget: HTMLAnchorElement;
  declare readonly shownSlugTarget: HTMLInputElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly hasSubmitTarget: boolean;
  declare readonly checkUrlValue: string;
  declare readonly timeoutValue: number;

  private bypass = false;
  private checking = false;

  async check(event: Event): Promise<void> {
    if (this.bypass) return;

    // Turbo's own busy state never engages here: preventDefault below means it
    // never starts a submission to disable the button for. So the check window
    // has to hold the button itself, or an impatient second click fires another
    // recommender call — and two that both come back empty both submit.
    if (this.checking) {
      event.preventDefault();
      return;
    }

    const title = this.fieldValue("support_request[title]");
    const text = this.fieldValue("support_request[text]");
    if (!title && !text) return;

    event.preventDefault();
    this.setChecking(true);

    const suggestion = await this.fetchSuggestion(title, text);

    if (!suggestion?.slug) {
      this.submitAnyway();
      return;
    }

    // Released only on the interstitial path. submitAnyway leaves it held: the
    // form is on its way, and re-enabling would reopen the same double-click.
    this.setChecking(false);
    this.present(suggestion);
  }

  // Records what they were shown before continuing, so the agent picking up the
  // ticket knows which article the customer already read and rejected.
  submitAnyway(): void {
    this.bypass = true;
    this.hide();
    this.element.requestSubmit();
  }

  dismiss(): void {
    this.hide();
    this.element.querySelector<HTMLTextAreaElement>('[name="support_request[text]"]')?.focus();
  }

  // aria-busy rather than a spinner: the button already carries
  // turbo_submits_with for the submission that follows, and the two states
  // should not disagree about what the button says.
  private setChecking(checking: boolean): void {
    this.checking = checking;
    if (!this.hasSubmitTarget) return;

    this.submitTarget.disabled = checking;
    this.submitTarget.setAttribute("aria-busy", String(checking));
  }

  // Covers the drawer/sheet rather than stacking a second overlay on it. The
  // form keeps its state underneath and is inerted while hidden, so tab order
  // stays inside the step — the containment a <dialog> used to give for free.
  private present(suggestion: Suggestion): void {
    this.titleTarget.textContent = suggestion.title;
    this.excerptTarget.textContent = suggestion.excerpt || "";
    this.reasonTarget.textContent = suggestion.reason || "";
    this.linkTarget.href = suggestion.url;
    this.shownSlugTarget.value = suggestion.slug;

    this.panelTarget.classList.remove("hidden");
    this.formChromeTarget.toggleAttribute("inert", true);
    this.panelTarget.querySelector("button")?.focus();
  }

  private hide(): void {
    this.panelTarget.classList.add("hidden");
    this.formChromeTarget.toggleAttribute("inert", false);
  }

  private fieldValue(name: string): string {
    const field = this.element.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`);
    return field?.value?.trim() || "";
  }

  private async fetchSuggestion(title: string, text: string): Promise<Suggestion | null> {
    const abort = new AbortController();
    const timer = window.setTimeout(() => abort.abort(), this.timeoutValue);

    try {
      const response = await fetch(this.checkUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        },
        body: JSON.stringify({ title, text }),
        signal: abort.signal,
      });

      if (!response.ok) return null;

      return (await response.json()) as Suggestion;
    } catch {
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  }
}

interface Suggestion {
  slug?: string;
  title: string;
  excerpt?: string;
  reason?: string;
  url: string;
}
